import { __decorate } from 'tslib';
import { ReactWrapperComponent, AngularReact, registerElement } from '@angular-react/core';
import * as i0 from '@angular/core';
import { EventEmitter, Component, ChangeDetectionStrategy, ViewChild, Input, Output, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { Styled } from '@angular-react/fabric/lib/utils';
import { CommonModule } from '@angular/common';
import { Panel } from '@fluentui/react/lib/Panel';

let FabPanelComponent = class FabPanelComponent extends ReactWrapperComponent {
    constructor(elementRef, changeDetectorRef, renderer, ngZone) {
        super(elementRef, changeDetectorRef, renderer, { ngZone });
        this.onLightDismissClick = new EventEmitter();
        this.onOpen = new EventEmitter();
        this.onOpened = new EventEmitter();
        this.onDismiss = new EventEmitter();
        this.onDismissed = new EventEmitter();
        // coming from React context - we need to bind to this so we can access the Angular Component properties
        this.onRenderHeader = this.onRenderHeader.bind(this);
        this.onDismissHandler = this.onDismissHandler.bind(this);
    }
    ngOnInit() {
        this.onRenderNavigation = this.createRenderPropHandler(this.renderNavigation);
        this.onRenderNavigationContent = this.createRenderPropHandler(this.renderNavigationContent);
        this._renderHeader = this.createInputJsxRenderer(this.renderHeader);
        this.onRenderBody = this.createRenderPropHandler(this.renderBody);
        this.onRenderFooter = this.createRenderPropHandler(this.renderFooter);
        this.onRenderFooterContent = this.createRenderPropHandler(this.renderFooterContent);
    }
    onRenderHeader(props, defaultRender, headerTextId) {
        if (!this.renderHeader) {
            return typeof defaultRender === 'function' ? defaultRender(props, defaultRender, headerTextId) : null;
        }
        return this._renderHeader({ props, headerTextId });
    }
    onDismissHandler(ev) {
        this.onDismiss.emit({
            ev: ev && ev.nativeEvent,
        });
    }
};
/** @nocollapse */ FabPanelComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabPanelComponent, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ FabPanelComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.1", type: FabPanelComponent, selector: "fab-panel", inputs: { componentRef: "componentRef", isOpen: "isOpen", hasCloseButton: "hasCloseButton", isLightDismiss: "isLightDismiss", isHiddenOnDismiss: "isHiddenOnDismiss", isBlocking: "isBlocking", isFooterAtBottom: "isFooterAtBottom", headerText: "headerText", styles: "styles", theme: "theme", className: "className", type: "type", customWidth: "customWidth", closeButtonAriaLabel: "closeButtonAriaLabel", headerClassName: "headerClassName", elementToFocusOnDismiss: "elementToFocusOnDismiss", ignoreExternalFocusing: "ignoreExternalFocusing", forceFocusInsideTrap: "forceFocusInsideTrap", firstFocusableSelector: "firstFocusableSelector", focusTrapZoneProps: "focusTrapZoneProps", layerProps: "layerProps", componentId: "componentId", renderNavigation: "renderNavigation", renderNavigationContent: "renderNavigationContent", renderHeader: "renderHeader", renderBody: "renderBody", renderFooter: "renderFooter", renderFooterContent: "renderFooterContent" }, outputs: { onLightDismissClick: "onLightDismissClick", onOpen: "onOpen", onOpened: "onOpened", onDismiss: "onDismiss", onDismissed: "onDismissed" }, viewQueries: [{ propertyName: "reactNodeRef", first: true, predicate: ["reactNode"], descendants: true, static: true }], exportAs: ["fabPanel"], usesInheritance: true, ngImport: i0, template: `
    <Panel
      #reactNode
      [componentRef]="componentRef"
      [isOpen]="isOpen"
      [hasCloseButton]="hasCloseButton"
      [isLightDismiss]="isLightDismiss"
      [isHiddenOnDismiss]="isHiddenOnDismiss"
      [isBlocking]="isBlocking"
      [isFooterAtBottom]="isFooterAtBottom"
      [headerText]="headerText"
      [styles]="styles"
      [theme]="theme"
      [className]="className"
      [type]="type"
      [customWidth]="customWidth"
      [closeButtonAriaLabel]="closeButtonAriaLabel"
      [headerClassName]="headerClassName"
      [elementToFocusOnDismiss]="elementToFocusOnDismiss"
      [ignoreExternalFocusing]="ignoreExternalFocusing"
      [forceFocusInsideTrap]="forceFocusInsideTrap"
      [firstFocusableSelector]="firstFocusableSelector"
      [focusTrapZoneProps]="focusTrapZoneProps"
      [layerProps]="layerProps"
      [componentId]="componentId"
      [RenderHeader]="renderHeader && onRenderHeader"
      [RenderNavigation]="renderNavigation && onRenderNavigation"
      [RenderNavigationContent]="renderNavigationContent && onRenderNavigationContent"
      [RenderBody]="renderBody && onRenderBody"
      [RenderFooter]="renderFooter && onRenderFooter"
      [RenderFooterContent]="renderFooterContent && onRenderFooterContent"
      [Dismiss]="onDismissHandler"
      (onOpen)="onOpen.emit($event)"
      (onOpened)="onOpened.emit($event)"
      (onDismissed)="onDismissed.emit($event)"
      (onLightDismissClick)="onLightDismissClick.emit($event)"
    >
      <ReactContent><ng-content></ng-content></ReactContent>
    </Panel>
  `, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
FabPanelComponent = __decorate([
    AngularReact(),
    Styled('FabPanelComponent')
], FabPanelComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabPanelComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fab-panel',
                    exportAs: 'fabPanel',
                    template: `
    <Panel
      #reactNode
      [componentRef]="componentRef"
      [isOpen]="isOpen"
      [hasCloseButton]="hasCloseButton"
      [isLightDismiss]="isLightDismiss"
      [isHiddenOnDismiss]="isHiddenOnDismiss"
      [isBlocking]="isBlocking"
      [isFooterAtBottom]="isFooterAtBottom"
      [headerText]="headerText"
      [styles]="styles"
      [theme]="theme"
      [className]="className"
      [type]="type"
      [customWidth]="customWidth"
      [closeButtonAriaLabel]="closeButtonAriaLabel"
      [headerClassName]="headerClassName"
      [elementToFocusOnDismiss]="elementToFocusOnDismiss"
      [ignoreExternalFocusing]="ignoreExternalFocusing"
      [forceFocusInsideTrap]="forceFocusInsideTrap"
      [firstFocusableSelector]="firstFocusableSelector"
      [focusTrapZoneProps]="focusTrapZoneProps"
      [layerProps]="layerProps"
      [componentId]="componentId"
      [RenderHeader]="renderHeader && onRenderHeader"
      [RenderNavigation]="renderNavigation && onRenderNavigation"
      [RenderNavigationContent]="renderNavigationContent && onRenderNavigationContent"
      [RenderBody]="renderBody && onRenderBody"
      [RenderFooter]="renderFooter && onRenderFooter"
      [RenderFooterContent]="renderFooterContent && onRenderFooterContent"
      [Dismiss]="onDismissHandler"
      (onOpen)="onOpen.emit($event)"
      (onOpened)="onOpened.emit($event)"
      (onDismissed)="onDismissed.emit($event)"
      (onLightDismissClick)="onLightDismissClick.emit($event)"
    >
      <ReactContent><ng-content></ng-content></ReactContent>
    </Panel>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }, { type: i0.NgZone }]; }, propDecorators: { reactNodeRef: [{
                type: ViewChild,
                args: ['reactNode', { static: true }]
            }], componentRef: [{
                type: Input
            }], isOpen: [{
                type: Input
            }], hasCloseButton: [{
                type: Input
            }], isLightDismiss: [{
                type: Input
            }], isHiddenOnDismiss: [{
                type: Input
            }], isBlocking: [{
                type: Input
            }], isFooterAtBottom: [{
                type: Input
            }], headerText: [{
                type: Input
            }], styles: [{
                type: Input
            }], theme: [{
                type: Input
            }], className: [{
                type: Input
            }], type: [{
                type: Input
            }], customWidth: [{
                type: Input
            }], closeButtonAriaLabel: [{
                type: Input
            }], headerClassName: [{
                type: Input
            }], elementToFocusOnDismiss: [{
                type: Input
            }], ignoreExternalFocusing: [{
                type: Input
            }], forceFocusInsideTrap: [{
                type: Input
            }], firstFocusableSelector: [{
                type: Input
            }], focusTrapZoneProps: [{
                type: Input
            }], layerProps: [{
                type: Input
            }], componentId: [{
                type: Input
            }], renderNavigation: [{
                type: Input
            }], renderNavigationContent: [{
                type: Input
            }], renderHeader: [{
                type: Input
            }], renderBody: [{
                type: Input
            }], renderFooter: [{
                type: Input
            }], renderFooterContent: [{
                type: Input
            }], onLightDismissClick: [{
                type: Output
            }], onOpen: [{
                type: Output
            }], onOpened: [{
                type: Output
            }], onDismiss: [{
                type: Output
            }], onDismissed: [{
                type: Output
            }] } });

// Copyright (c) Microsoft Corporation. All rights reserved.
const components = [FabPanelComponent];
class FabPanelModule {
    constructor() {
        // Add any React elements to the registry (used by the renderer).
        registerElement('Panel', () => Panel);
    }
}
/** @nocollapse */ FabPanelModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabPanelModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ FabPanelModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabPanelModule, declarations: [FabPanelComponent], imports: [CommonModule], exports: [FabPanelComponent] });
/** @nocollapse */ FabPanelModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabPanelModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabPanelModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: components,
                    exports: components,
                    schemas: [NO_ERRORS_SCHEMA],
                }]
        }], ctorParameters: function () { return []; } });

// Copyright (c) Microsoft Corporation. All rights reserved.

/**
 * Generated bundle index. Do not edit.
 */

export { FabPanelComponent, FabPanelModule };
